'use client'

import { useEffect, useState } from 'react'

import type { WeedIdCapability } from '@/lib/analysis/weed-id-capability'

const DISABLED: WeedIdCapability = {
  analysisEnabled: false,
  recoveryEnabled: false,
  productShelfEnabled: false,
  nurtureEnabled: false,
  indexable: false,
  cohort: 'off',
  unavailableReason: 'dependency_missing',
}

let cached: WeedIdCapability | null = null
let inFlight: Promise<WeedIdCapability> | null = null

async function loadCapability(): Promise<WeedIdCapability> {
  if (cached) return cached
  if (!inFlight) {
    inFlight = fetch('/api/weed-identifier/capability', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(async (response) => response.ok ? await response.json() as WeedIdCapability : DISABLED)
      .catch(() => DISABLED)
      .then((value) => {
        cached = value
        return value
      })
      .finally(() => { inFlight = null })
  }
  return inFlight
}

export function resetWeedIdCapabilityCacheForTests() {
  cached = null
  inFlight = null
}

export function useWeedIdCapability() {
  const [capability, setCapability] = useState<WeedIdCapability | null>(cached)
  useEffect(() => {
    let active = true
    void loadCapability().then((value) => { if (active) setCapability(value) })
    return () => { active = false }
  }, [])
  return {
    capability: capability ?? DISABLED,
    isLoading: capability === null,
  }
}
